// src/components/store/boost/BoostGuide.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  TrendingUp, 
  Target, 
  BarChart, 
  Clock, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  DollarSign,
  Eye,
  Users,
  Zap,
  Calendar,
  Award,
  HelpCircle,
  RefreshCw,
  ChevronRight,
  Star,
  Shield,
  Timer,
  Percent
} from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import { useAuth } from '../../../context/AuthContext';
import MobileBottomNav from '../../../components/layout/MobileBottomNav';
import Loading from '../../../components/common/Loading';
import Button from '../../../components/common/Button';
import { getBoostPackages } from '../../../services/api';

const BoostGuide = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(null);
  const [activeTab, setActiveTab] = useState('benefits');
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [error, setError] = useState(null);

  const brandColor = '#ff660e';
  const brandColorLight = '#fff0e6';

  useEffect(() => {
    fetchBoostPackages();
  }, []);

  const fetchBoostPackages = async () => {
    setLoading(true);
    try {
      const response = await getBoostPackages();
      if (response.success) {
        setPackages(response.data || []);
      } else {
        setError('Failed to load boost packages. Please try again.');
      }
    } catch (error) {
      // console.error('Error fetching boost packages:', error);
      setError('Unable to load boost information. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  const boostBenefits = [
    {
      id: 1,
      title: "Top Search Placement",
      description: "Your listing appears at the top of search results and category pages",
      icon: <Target className="w-6 h-6" />,
      metrics: "5-10x more visibility",
      details: [
        "Priority placement in relevant search results",
        "Featured position in category listings",
        "Increased exposure to targeted buyers",
        "Higher click-through rates"
      ],
      iconColor: "text-blue-500",
      bgColor: "bg-blue-50",
      stats: "85% of buyers click on top 3 results"
    },
    {
      id: 2,
      title: "Increased Views & Clicks",
      description: "Get significantly more eyeballs on your products",
      icon: <Eye className="w-6 h-6" />,
      metrics: "300-500% more views",
      details: [
        "Stand out from competing listings",
        "Attract more profile visits",
        "Increase message inquiries",
        "Build seller credibility"
      ],
      iconColor: "text-purple-500",
      bgColor: "bg-purple-50",
      stats: "Boosted listings get 3x more messages"
    },
    {
      id: 3,
      title: "Faster Sales",
      description: "Sell your items quicker without compromising on price",
      icon: <Zap className="w-6 h-6" />,
      metrics: "2-3x faster sales",
      details: [
        "Reduce time to sell by 50-70%",
        "Attract serious buyers faster",
        "Multiple offers on popular items",
        "Less price negotiation pressure"
      ],
      iconColor: "text-green-500",
      bgColor: "bg-green-50",
      stats: "Average sale time reduced by 65%"
    },
    {
      id: 4,
      title: "Higher Selling Price",
      description: "Get closer to your asking price with competitive visibility",
      icon: <DollarSign className="w-6 h-6" />,
      metrics: "10-15% better price",
      details: [
        "More buyer competition = better prices",
        "Less need for price drops",
        "Attract buyers willing to pay premium",
        "Build value perception"
      ],
      iconColor: "text-yellow-500",
      bgColor: "bg-yellow-50",
      stats: "Average 12% higher sale price"
    }
  ];

  // Map API packages to component format
  const apiBoostPackages = packages.map(pkg => ({
    id: pkg.package_id,
    name: pkg.name,
    duration: `${pkg.duration_days} days`,
    price: `₦${parseInt(pkg.price || 0).toLocaleString()}`,
    original_price: pkg.original_price ? `₦${parseInt(pkg.original_price).toLocaleString()}` : null,
    features: Array.isArray(pkg.features) ? pkg.features : (pkg.features ? JSON.parse(pkg.features) : []),
    bestFor: pkg.best_for || "All types of listings",
    color: pkg.is_popular ? "border-orange-300" : "border-gray-300",
    popular: pkg.is_popular,
    data: pkg
  }));

  const faqs = [
    {
      question: "How long does it take for boost to activate?",
      answer: "Boost activation is instant. Your listing will appear in boosted positions within 5-10 minutes of payment confirmation."
    },
    {
      question: "Can I boost multiple listings at once?",
      answer: "Yes! You can boost as many listings as you want simultaneously. Each listing requires its own boost package."
    },
    {
      question: "What happens when boost expires?",
      answer: "Your listing returns to normal visibility. You can purchase another boost package anytime to regain premium positioning."
    },
    {
      question: "Can I get a refund if my item doesn't sell?",
      answer: "Boost packages are non-refundable as they provide guaranteed visibility, not guaranteed sales. However, you can modify or cancel future boosts anytime."
    },
    {
      question: "How do I choose the right package?",
      answer: "Consider your item's value, competition, and how quickly you want to sell. Premium items benefit more from longer boosts."
    }
  ];

  const bestPractices = [
    {
      title: "Optimize Before Boosting",
      tips: [
        "Ensure photos are high-quality and well-lit",
        "Write detailed, honest descriptions",
        "Set competitive but fair pricing",
        "Include all relevant specifications"
      ]
    },
    {
      title: "Timing is Everything",
      tips: [
        "Boost on weekends for maximum visibility",
        "Start boost when you're available to respond quickly",
        "Consider seasonal demand patterns",
        "Avoid boosting during major holidays (if shipping)"
      ]
    },
    {
      title: "Monitor & Adjust",
      tips: [
        "Check analytics daily",
        "Respond to messages within 2 hours",
        "Be ready to negotiate",
        "Consider price adjustments if no interest"
      ]
    },
    {
      title: "Combine Strategies",
      tips: [
        "Use social media alongside boosting",
        "Share your boosted listing in relevant groups",
        "Offer bundle deals to boost customers",
        "Include free delivery as incentive"
      ]
    }
  ];

  const handleStartBoosting = () => {
    if (user) {
      navigate('/store/boost-package/listings');
    } else {
      navigate('/login', { state: { redirectTo: '/store/boost-package/listings' } });
    }
  };

  const handleViewBoosted = () => {
    if (user) {
      navigate('/store/boost-history');
    } else {
      navigate('/login');
    }
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return '₦0';
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-50/50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loading size="large" />
          <p className="mt-4 text-gray-600">Loading boost guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50/50 to-white">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Hero Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-24 h-24 rounded-3xl mb-6 relative overflow-hidden"
               style={{ 
                 backgroundColor: brandColorLight,
                 boxShadow: `0 10px 40px ${brandColor}30`
               }}>
            <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-orange-200 opacity-50"></div>
            <TrendingUp className="w-12 h-12 relative z-10" style={{ color: brandColor }} />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            <span className="bg-gradient-to-r from-orange-600 to-orange-400 bg-clip-text text-transparent">
              Boost Guide
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Maximize your sales potential with our powerful boosting features. 
            Learn how to get more views, faster sales, and better prices.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-800">{error}</p>
              </div>
              <button
                onClick={fetchBoostPackages}
                className="flex items-center gap-2 px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "More Views", value: "300%", icon: <Eye className="w-5 h-5" /> },
            { label: "Faster Sales", value: "2.5x", icon: <Zap className="w-5 h-5" /> },
            { label: "Higher Prices", value: "+12%", icon: <DollarSign className="w-5 h-5" /> },
            { label: "Satisfaction", value: "94%", icon: <Star className="w-5 h-5" /> }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <div className="p-2 rounded-lg" style={{ backgroundColor: brandColorLight }}>
                  <div style={{ color: brandColor }}>{stat.icon}</div>
                </div>
                <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
              </div>
              <p className="text-sm text-gray-600">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-2xl shadow-sm p-2 mb-8">
          <div className="flex flex-wrap gap-2">
            {[
              { id: 'benefits', label: 'Benefits' },
              { id: 'packages', label: 'Packages' },
              { id: 'best-practices', label: 'Best Practices' },
              { id: 'faq', label: 'FAQ' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                  activeTab === tab.id
                    ? 'text-white'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                style={{ 
                  backgroundColor: activeTab === tab.id ? brandColor : 'transparent'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === 'benefits' && (
          <div className="space-y-8">
            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {boostBenefits.map((benefit) => (
                <div
                  key={benefit.id}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className={`p-3 rounded-xl ${benefit.bgColor}`}>
                      <div className={benefit.iconColor}>{benefit.icon}</div>
                    </div>
                    <span className="text-sm font-semibold px-3 py-1 rounded-full bg-orange-100 text-orange-700">
                      {benefit.metrics}
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 mb-4">{benefit.description}</p>
                  
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                      What you get:
                    </h4>
                    <ul className="space-y-2">
                      {benefit.details.map((detail, index) => (
                        <li key={index} className="flex items-start text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="mt-6 pt-4 border-t border-gray-100">
                    <p className="text-sm font-medium text-gray-500">
                      📊 {benefit.stats}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* How It Works */}
            <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                How Boosting Works
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {[
                  {
                    step: 1,
                    title: "Select Listing",
                    description: "Choose item from your active listings"
                  },
                  {
                    step: 2,
                    title: "Pick Package",
                    description: "Choose boost duration & features"
                  },
                  {
                    step: 3,
                    title: "Make Payment",
                    description: "Secure payment via wallet or card"
                  },
                  {
                    step: 4,
                    title: "Go Live",
                    description: "Instant activation, track results"
                  }
                ].map((step) => (
                  <div key={step.step} className="text-center">
                    <div className="inline-flex items-center justify-center w-14 h-14 rounded-full text-white text-xl font-bold mb-4 mx-auto"
                         style={{ backgroundColor: brandColor }}>
                      {step.step}
                    </div>
                    <h3 className="font-bold text-gray-900 mb-2">{step.title}</h3>
                    <p className="text-sm text-gray-600">{step.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'packages' && (
          <div className="space-y-8">
            {/* Package Comparison */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Choose Your Boost Package
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Select the perfect boost for your needs. All packages include our 
                core visibility features with additional benefits at higher tiers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {apiBoostPackages.map((pkg) => (
                <div
                  key={pkg.id}
                  className={`bg-white rounded-2xl shadow-sm border-2 p-6 relative transition-all hover:shadow-lg ${
                    pkg.popular ? 'border-orange-300 ring-2 ring-orange-300 ring-offset-2' : 'border-gray-300'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                    <div className="mb-4">
                      <span className="text-4xl font-bold text-gray-900">{pkg.price}</span>
                      <span className="text-gray-600 ml-2">for {pkg.duration}</span>
                    </div>
                    {pkg.original_price && (
                      <div className="mb-2">
                        <span className="text-sm text-gray-400 line-through mr-2">
                          {pkg.original_price}
                        </span>
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          Save {formatPrice(parseInt(pkg.data.original_price) - parseInt(pkg.data.price))}
                        </span>
                      </div>
                    )}
                    <div className="text-sm text-gray-500">
                      Best for: {pkg.bestFor}
                    </div>
                  </div>
                  
                  <div className="space-y-3 mb-8">
                    {pkg.features.map((feature, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Button
                    onClick={handleStartBoosting}
                    variant={pkg.popular ? "primary" : "outline"}
                    fullWidth
                  >
                    Select Package
                  </Button>
                </div>
              ))}
            </div>           
          </div>
        )}

        {activeTab === 'best-practices' && (
          <div className="space-y-8">
            {/* Best Practices Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {bestPractices.map((practice, index) => (
                <div key={index} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center mr-4"
                         style={{ backgroundColor: brandColorLight }}>
                      <span className="font-bold" style={{ color: brandColor }}>{index + 1}</span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">{practice.title}</h3>
                  </div>
                  
                  <div className="space-y-3">
                    {practice.tips.map((tip, tipIndex) => (
                      <div key={tipIndex} className="flex items-start bg-gray-50 rounded-lg p-3">
                        <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{tip}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Success Checklist */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Boost Success Checklist</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { text: "High-quality photos (min 3)", checked: true },
                  { text: "Detailed description", checked: true },
                  { text: "Competitive pricing", checked: true },
                  { text: "Quick response time", checked: false },
                  { text: "Active during boost period", checked: false },
                  { text: "Ready to negotiate", checked: false }
                ].map((item, index) => (
                  <div key={index} className="flex items-center">
                    <div className={`w-6 h-6 rounded-full mr-3 flex items-center justify-center ${
                      item.checked 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-400'
                    }`}>
                      {item.checked ? '✓' : ''}
                    </div>
                    <span className={`${
                      item.checked ? 'text-gray-900' : 'text-gray-500'
                    }`}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'faq' && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
                
                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="border border-gray-200 rounded-xl overflow-hidden"
                    >
                      <button
                        onClick={() => setActiveStep(activeStep === index ? null : index)}
                        className="w-full p-5 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                      >
                        <h3 className="text-lg font-semibold text-gray-900 pr-4">
                          {faq.question}
                        </h3>
                        <ChevronRight className={`w-5 h-5 text-gray-400 transition-transform ${
                          activeStep === index ? 'rotate-90' : ''
                        }`} />
                      </button>
                      
                      {activeStep === index && (
                        <div className="p-5 pt-0">
                          <div className="pl-4 border-l-2 border-orange-500">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Still Have Questions */}
            <div className="text-center">
              <div className="inline-flex flex-col items-center p-8 rounded-2xl max-w-2xl mx-auto"
                   style={{ backgroundColor: brandColorLight }}>
                <HelpCircle className="w-16 h-16 mb-4" style={{ color: brandColor }} />
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Still Have Questions?</h2>
                <p className="text-gray-600 mb-6 max-w-md">
                  Our seller support team is here to help you succeed.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Button
                    onClick={() => navigate('/support')}
                    variant="outline"
                  >
                    Contact Support
                  </Button>
                  <Button
                    onClick={handleStartBoosting}
                    variant="primary"
                  >
                    Start Boosting Now
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA Section */}
        {(activeTab !== 'faq') && (
          <div className="mt-12">
            <div className="rounded-2xl p-8 text-center relative overflow-hidden"
                 style={{ 
                   backgroundColor: brandColor,
                   color: 'white'
                 }}>
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{
                  backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)`
                }}></div>
              </div>
              
              <div className="relative z-10">
                <TrendingUp className="w-16 h-16 mx-auto mb-6 opacity-90" />
                <h2 className="text-3xl font-bold mb-4">Ready to Boost Your Sales?</h2>
                <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
                  Join thousands of successful sellers who are already benefiting from our boost feature.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button
                    onClick={handleStartBoosting}
                    variant="secondary"
                    className="bg-white text-orange-600 hover:bg-gray-100"
                  >
                    Start Boosting Now
                  </Button>
                  {user && (
                    <Button
                      onClick={handleViewBoosted}
                      variant="outline"
                      className="border-2 border-white text-white hover:bg-white/10"
                    >
                      View My Boosted Listings
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
      <MobileBottomNav />
    </div>
  );
};

export default BoostGuide;